// src/components/Courses.js
import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Modal,
  Box,
  TextField,
  IconButton,
  Container,
  Card,
  CardContent,
  Fade,
  Alert,
  Snackbar,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Avatar,
  Chip,
} from '@mui/material';
import {
  Add,
  Delete,
  Logout,
  Dashboard,
  Close,
  CheckCircle,
  Error as ErrorIcon,
  ArrowBack,
  Edit,
  School,
  VideoLibrary,
} from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import { ref, set, get, child, remove } from 'firebase/database';
import { auth, database } from '../firebase/Config';
import { useNavigate } from 'react-router-dom';

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 600,
  maxHeight: '90vh',
  overflowY: 'auto',
  bgcolor: 'background.paper',
  borderRadius: '16px',
  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
  p: 4,
};

const Courses = () => {
  const [courses, setCourses] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [courseData, setCourseData] = useState({
    title: '',
    description: '',
    thumbnail: '',
    instructor: '',
    duration: '',
  });
  const [userEmail, setUserEmail] = useState(null);
  const navigate = useNavigate();

  const [errors, setErrors] = useState({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    courseId: null,
    courseTitle: '',
  });

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const validateForm = () => {
    const newErrors = {};

    if (!courseData.title.trim()) {
      newErrors.title = 'Título é obrigatório';
    }

    if (!courseData.description.trim()) {
      newErrors.description = 'Descrição é obrigatória';
    }

    if (!courseData.instructor.trim()) {
      newErrors.instructor = 'Instrutor é obrigatório';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleOpen = () => {
    setErrors({});
    setIsEditing(false);
    setCourseData({
      title: '',
      description: '',
      thumbnail: '',
      instructor: '',
      duration: '',
    });
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setIsEditing(false);
    setErrors({});
    setCourseData({
      title: '',
      description: '',
      thumbnail: '',
      instructor: '',
      duration: '',
    });
  };

  useEffect(() => {
    const user = auth.currentUser;
    if (user) {
      setUserEmail(user.email);
    }
  }, []);

  const fetchCourses = async () => {
    setLoading(true);
    const dbRef = ref(database);
    try {
      const snapshot = await get(child(dbRef, 'courses'));
      if (snapshot.exists()) {
        const coursesData = snapshot.val();
        const coursesArray = Object.keys(coursesData).map((key) => ({
          id: key,
          ...coursesData[key],
        }));
        setCourses(coursesArray);
      } else {
        setCourses([]);
      }
    } catch (error) {
      console.error('Erro ao buscar cursos: ', error);
      showSnackbar('Erro ao carregar cursos', 'error');
    }
    setLoading(false);
  };

  const handleAddCourse = async () => {
    if (!validateForm()) {
      showSnackbar('Por favor, corrija os erros no formulário', 'error');
      return;
    }

    setSubmitLoading(true);
    try {
      const courseId = isEditing ? courseData.id : `course_${new Date().getTime()}`;

      await set(ref(database, `courses/${courseId}`), {
        title: courseData.title,
        description: courseData.description,
        thumbnail: courseData.thumbnail,
        instructor: courseData.instructor,
        duration: courseData.duration,
        createdBy: isEditing ? courseData.createdBy : userEmail,
        createdAt: isEditing ? courseData.createdAt : new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      });

      showSnackbar(
        isEditing ? 'Curso atualizado com sucesso!' : 'Curso cadastrado com sucesso!',
        'success'
      );
      handleClose();
      fetchCourses();
    } catch (error) {
      console.error('Erro ao salvar curso: ', error);
      showSnackbar('Erro ao salvar curso', 'error');
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleEdit = (course) => {
    setCourseData(course);
    setIsEditing(true);
    setOpen(true);
  };

  const handleOpenDeleteDialog = (course) => {
    setDeleteDialog({
      open: true,
      courseId: course.id,
      courseTitle: course.title,
    });
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialog({ open: false, courseId: null, courseTitle: '' });
  };

  const handleDeleteCourse = async () => {
    try {
      await remove(ref(database, `courses/${deleteDialog.courseId}`));
      
      // Também remover todas as aulas deste curso
      const lessonsSnapshot = await get(child(ref(database), 'lessons'));
      if (lessonsSnapshot.exists()) {
        const lessonsData = lessonsSnapshot.val();
        const deletePromises = Object.keys(lessonsData)
          .filter(key => lessonsData[key].courseId === deleteDialog.courseId)
          .map(key => remove(ref(database, `lessons/${key}`)));
        
        await Promise.all(deletePromises);
      }

      showSnackbar('Curso excluído com sucesso!', 'success');
      fetchCourses();
      handleCloseDeleteDialog();
    } catch (error) {
      console.error('Erro ao excluir curso: ', error);
      showSnackbar('Erro ao excluir curso', 'error');
    }
  };

  const handleManageLessons = (courseId) => {
    navigate(`/admin/lessons/${courseId}`);
  };

  const handleLogout = async () => {
    await auth.signOut();
    navigate('/login');
  };

  useEffect(() => {
    fetchCourses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const columns = [
    {
      field: 'title',
      headerName: 'Título do Curso',
      width: 300,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <School sx={{ color: '#48bb78', fontSize: 20 }} />
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'instructor',
      headerName: 'Instrutor',
      width: 200,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => (
        <Chip
          avatar={<Avatar sx={{ bgcolor: '#48bb78' }}>{params.value?.charAt(0).toUpperCase()}</Avatar>}
          label={params.value}
          size="small"
        />
      ),
    },
    {
      field: 'duration',
      headerName: 'Duração',
      width: 120,
      headerAlign: 'center',
      align: 'center',
    },
    {
      field: 'createdAt',
      headerName: 'Data de Criação',
      width: 180,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => {
        if (!params.value) return '-';
        const date = new Date(params.value);
        return date.toLocaleDateString('pt-BR');
      },
    },
    {
      field: 'actions',
      headerName: 'Ações',
      width: 250,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<VideoLibrary />}
            onClick={() => handleManageLessons(params.row.id)}
            sx={{
              borderColor: '#48bb78',
              color: '#48bb78',
              '&:hover': { borderColor: '#38a169', bgcolor: '#f7fafc' },
            }}
          >
            Aulas
          </Button>
          <IconButton
            onClick={() => handleEdit(params.row)}
            sx={{ color: '#48bb78', '&:hover': { bgcolor: '#48bb7820' } }}
          >
            <Edit />
          </IconButton>
          <IconButton
            onClick={() => handleOpenDeleteDialog(params.row)}
            sx={{ color: '#e53935', '&:hover': { bgcolor: '#e5393520' } }}
          >
            <Delete />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ minHeight: '100vh', background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)' }}>
      <AppBar
        position="static"
        elevation={0}
        sx={{
          background: 'linear-gradient(90deg, #48bb78 0%, #38a169 100%)',
        }}
      >
        <Toolbar sx={{ py: 1 }}>
          <IconButton
            onClick={() => navigate('/home')}
            sx={{
              color: '#fff',
              mr: 2,
              '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.1)' }
            }}
          >
            <ArrowBack />
          </IconButton>
          <Dashboard sx={{ mr: 2, fontSize: 32 }} />
          <Typography
            variant="h5"
            sx={{
              flexGrow: 1,
              fontWeight: 700,
              letterSpacing: '0.5px',
            }}
          >
            Gerenciar Cursos
          </Typography>
          {userEmail && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Chip
                avatar={<Avatar sx={{ bgcolor: '#fff', color: '#48bb78' }}>{userEmail.charAt(0).toUpperCase()}</Avatar>}
                label={userEmail}
                sx={{
                  bgcolor: 'rgba(255, 255, 255, 0.2)',
                  color: '#fff',
                  fontWeight: 500,
                  px: 1,
                }}
              />
              <IconButton
                onClick={handleLogout}
                sx={{
                  color: '#fff',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.1)',
                  },
                }}
              >
                <Logout />
              </IconButton>
            </Box>
          )}
        </Toolbar>
      </AppBar>

      <Container maxWidth="xl" sx={{ mt: 4, pb: 4 }}>
        <Fade in timeout={600}>
          <Card
            elevation={0}
            sx={{
              borderRadius: 4,
              overflow: 'hidden',
              border: '1px solid rgba(255, 255, 255, 0.3)',
            }}
          >
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 700,
                      color: '#2d3748',
                      mb: 1,
                    }}
                  >
                    <School sx={{ mr: 1, verticalAlign: 'middle', fontSize: 32, color: '#48bb78' }} />
                    Cursos Disponíveis
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#718096' }}>
                    Total de cursos: <strong>{courses.length}</strong>
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<Add />}
                  onClick={handleOpen}
                  sx={{
                    background: 'linear-gradient(135deg, #48bb78 0%, #38a169 100%)',
                    color: '#fff',
                    fontWeight: 600,
                    py: 1.5,
                    px: 3,
                    borderRadius: 2,
                    textTransform: 'none',
                    boxShadow: '0 4px 15px rgba(72, 187, 120, 0.4)',
                    '&:hover': {
                      boxShadow: '0 6px 20px rgba(72, 187, 120, 0.6)',
                      transform: 'translateY(-2px)',
                    },
                    transition: 'all 0.3s',
                  }}
                >
                  Novo Curso
                </Button>
              </Box>

              <Box sx={{ height: 600, width: '100%' }}>
                <DataGrid
                  rows={courses}
                  columns={columns}
                  pageSize={10}
                  rowsPerPageOptions={[5, 10, 20, 50]}
                  checkboxSelection
                  disableSelectionOnClick
                  loading={loading}
                  sx={{
                    border: 'none',
                    '& .MuiDataGrid-cell': {
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    },
                    '& .MuiDataGrid-cell:focus': {
                      outline: 'none',
                    },
                    '& .MuiDataGrid-row:hover': {
                      bgcolor: '#f7fafc',
                    },
                    '& .MuiDataGrid-columnHeaders': {
                      bgcolor: '#f7fafc',
                      fontSize: '0.95rem',
                      fontWeight: 600,
                    },
                  }}
                />
              </Box>
            </CardContent>
          </Card>
        </Fade>

        {/* Modal de Novo/Editar Curso */}
        <Modal open={open} onClose={handleClose}>
          <Fade in={open}>
            <Box sx={modalStyle}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    color: '#2d3748',
                  }}
                >
                  {isEditing ? 'Editar Curso' : 'Adicionar Novo Curso'}
                </Typography>
                <IconButton onClick={handleClose} sx={{ color: '#718096' }}>
                  <Close />
                </IconButton>
              </Box>

              <TextField
                label="Título do Curso"
                fullWidth
                margin="normal"
                required
                value={courseData.title}
                onChange={(e) => {
                  setCourseData({ ...courseData, title: e.target.value });
                  if (errors.title) setErrors({ ...errors, title: null });
                }}
                error={!!errors.title}
                helperText={errors.title}
              />

              <TextField
                label="Descrição"
                fullWidth
                margin="normal"
                required
                multiline
                rows={4}
                value={courseData.description}
                onChange={(e) => {
                  setCourseData({ ...courseData, description: e.target.value });
                  if (errors.description) setErrors({ ...errors, description: null });
                }}
                error={!!errors.description}
                helperText={errors.description}
              />

              <TextField
                label="Instrutor"
                fullWidth
                margin="normal"
                required
                value={courseData.instructor}
                onChange={(e) => {
                  setCourseData({ ...courseData, instructor: e.target.value });
                  if (errors.instructor) setErrors({ ...errors, instructor: null });
                }}
                error={!!errors.instructor}
                helperText={errors.instructor}
              />

              <TextField
                label="URL da Thumbnail (opcional)"
                fullWidth
                margin="normal"
                value={courseData.thumbnail}
                onChange={(e) => setCourseData({ ...courseData, thumbnail: e.target.value })}
                helperText="URL da imagem de capa do curso"
              />

              <TextField
                label="Duração Estimada (opcional)"
                fullWidth
                margin="normal"
                value={courseData.duration}
                onChange={(e) => setCourseData({ ...courseData, duration: e.target.value })}
                placeholder="Ex: 4 horas"
                helperText="Exemplo: 4 horas, 30 minutos, etc."
              />

              <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={handleClose}
                  sx={{
                    py: 1.5,
                    borderColor: '#718096',
                    color: '#718096',
                    '&:hover': {
                      borderColor: '#2d3748',
                      bgcolor: '#f7fafc',
                    },
                  }}
                >
                  Cancelar
                </Button>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleAddCourse}
                  disabled={submitLoading}
                  sx={{
                    py: 1.5,
                    background: 'linear-gradient(135deg, #48bb78 0%, #38a169 100%)',
                    color: '#fff',
                    fontWeight: 600,
                    '&:hover': {
                      boxShadow: '0 6px 20px rgba(72, 187, 120, 0.4)',
                    },
                    '&:disabled': {
                      background: '#cbd5e0',
                    },
                  }}
                >
                  {submitLoading ? (
                    <CircularProgress size={24} sx={{ color: '#fff' }} />
                  ) : (
                    <>
                      <CheckCircle sx={{ mr: 1 }} />
                      {isEditing ? 'Atualizar' : 'Cadastrar'}
                    </>
                  )}
                </Button>
              </Box>
            </Box>
          </Fade>
        </Modal>

        {/* Dialog de Confirmação de Exclusão */}
        <Dialog
          open={deleteDialog.open}
          onClose={handleCloseDeleteDialog}
          PaperProps={{
            sx: { borderRadius: 3 }
          }}
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ErrorIcon sx={{ color: '#e53935' }} />
            Confirmar Exclusão
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              Tem certeza que deseja excluir o curso <strong>{deleteDialog.courseTitle}</strong>?
              <br /><br />
              <strong>Atenção:</strong> Todas as aulas deste curso também serão excluídas.
              <br /><br />
              Esta ação não pode ser desfeita.
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button
              onClick={handleCloseDeleteDialog}
              sx={{ color: '#718096' }}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleDeleteCourse}
              variant="contained"
              sx={{
                bgcolor: '#e53935',
                '&:hover': { bgcolor: '#c62828' }
              }}
              startIcon={<Delete />}
            >
              Excluir
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar para Feedback */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            variant="filled"
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default Courses;
